import React from 'react';
import { Component } from '../components/Component';
import Container from '../components/Container';
import SQLEditor from '../components/sql-trainer/SQLEditor';
import SQLResultsTable from '../components/sql-trainer/SQLResultsTable';
import QuestionTab from '../components/sql-trainer/QuestionTab';
import SchemaChatSplit from '../components/sql-trainer/SchemaChatSplit';
import { SQL_TESTS, DEFAULT_TEST, getTest, getTestMetadata } from '../data/sqlTests';
import { initialiseDatabase, executeQuery, compareResults, resetDatabase } from '../utils/SQLUtils';
import { sendChatMessage } from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'sql-formatter';

// Constants
const STORAGE_KEY = 'sqlTrainerQueries';
const CHAT_STORAGE_KEY = 'sqlTrainerConversations';

/**
 * Main SQL Trainer page
 * Supports multiple test sets with different schemas and questions
 * Features: query execution, validation, diff display, localStorage persistence
 */
class SQLTrainer extends Component {
  constructor(props) {
    super(props);

    // Parse URL parameters
    const { testId, questionId } = this.getURLParams();

    // Load saved queries and initialise user queries
    const savedQueries = this.loadSavedQueries();
    const userQueries = this.initializeUserQueries(savedQueries);

    // Load saved conversations
    const savedConversations = this.loadSavedConversations();

    const question = this.getQuestionForInit(testId, questionId);

    this.state = {
      currentTestId: testId,
      currentQuestionId: questionId,
      userQueries,
      queryResults: null,
      referenceResults: null,
      verdict: null,
      diff: null,
      dbInitialised: false,
      error: null,
      executing: false,
      timeRemaining: question?.timeLimit || 0,
      chatMessages: savedConversations,  // Load from localStorage
      chatOpen: false,       // Chat panel visibility
      chatLoading: false     // API call in progress
    };

    this.timerInterval = null;
  }

  async componentDidMount() {
    await this.initialiseTestDatabase(this.state.currentTestId);
    this.startTimer();
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  /**
   * Get question for initialization (before component mounts)
   */
  getQuestionForInit = (testId, questionId) => {
    const test = getTest(testId);
    return test.questions.find(q => q.id === questionId);
  };

  /**
   * Parse URL parameters for test and question IDs
   */
  getURLParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      testId: urlParams.get('test') || DEFAULT_TEST,
      questionId: parseInt(urlParams.get('q')) || 1
    };
  };

  /**
   * Initialise user queries with saved queries or empty strings
   */
  initializeUserQueries = (savedQueries) => {
    const userQueries = {};
    Object.keys(SQL_TESTS).forEach(testId => {
      userQueries[testId] = {};
      SQL_TESTS[testId].questions.forEach(q => {
        userQueries[testId][q.id] = savedQueries[testId]?.[q.id] || '';
      });
    });
    return userQueries;
  };

  /**
   * Get the current question object
   */
  getCurrentQuestion = () => {
    const { currentTestId, currentQuestionId } = this.state;
    const test = getTest(currentTestId);
    return test.questions.find(q => q.id === currentQuestionId);
  };

  /**
   * Clear execution state (results, verdict, errors)
   */
  clearExecutionState = () => {
    this.setState({
      queryResults: null,
      referenceResults: null,
      verdict: null,
      diff: null,
      error: null
    });
  };

  /**
   * Start countdown timer
   */
  startTimer = () => {
    this.stopTimer();
    this.timerInterval = setInterval(() => {
      this.setState(prevState => ({
        timeRemaining: Math.max(0, prevState.timeRemaining - 1)
      }));
    }, 1000);
  };

  /**
   * Stop countdown timer
   */
  stopTimer = () => {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  };

  /**
   * Reset timer to question's time limit
   */
  resetTimer = () => {
    const question = this.getCurrentQuestion();
    if (question) {
      this.setState({ timeRemaining: question.timeLimit || 0 });
      this.startTimer();
    }
  };

  /**
   * Check if controls should be disabled
   */
  isDisabled = () => {
    const { dbInitialised, executing } = this.state;
    return !dbInitialised || executing;
  };

  /**
   * Initialise database for a test
   */
  initialiseTestDatabase = async (testId) => {
    this.setState({ dbInitialised: false, error: null });

    try {
      // Reset previous database
      resetDatabase();

      const test = getTest(testId);
      await initialiseDatabase(test.schema, test.seedDataFn);

      this.setState({ dbInitialised: true });
    } catch (err) {
      console.error('Database initialisation failed:', err);
      this.setState({
        error: `Failed to initialise database: ${err.message}`,
        dbInitialised: false
      });
    }
  };

  /**
   * Load saved queries from localStorage
   */
  loadSavedQueries = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load saved queries:', err);
      return {};
    }
  };

  /**
   * Save queries to localStorage
   */
  saveQueries = (queries) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    } catch (err) {
      console.error('Failed to save queries:', err);
    }
  };

  /**
   * Load saved conversations from localStorage
   */
  loadSavedConversations = () => {
    try {
      const saved = localStorage.getItem(CHAT_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load conversations:', err);
      return {};
    }
  };

  /**
   * Save conversations to localStorage
   */
  saveConversations = (conversations) => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(conversations));
    } catch (err) {
      console.error('Failed to save conversations:', err);
    }
  };

  /**
   * Update URL with current test and question
   */
  updateURL = () => {
    const { currentTestId, currentQuestionId } = this.state;
    const url = `/sql-trainer?test=${currentTestId}&q=${currentQuestionId}`;
    window.history.pushState({}, '', url);
  };

  /**
   * Handle test selection change
   */
  handleTestChange = async (e) => {
    const newTestId = e.target.value;
    await this.initialiseTestDatabase(newTestId);
    const question = getTest(newTestId).questions.find(q => q.id === 1);
    this.setState({
      currentTestId: newTestId,
      currentQuestionId: 1,
      timeRemaining: question?.timeLimit || 0
    }, () => {
      this.clearExecutionState();
      this.updateURL();
      this.resetTimer();
    });
  };

  /**
   * Handle question tab click
   */
  handleQuestionChange = (questionId) => {
    const question = getTest(this.state.currentTestId).questions.find(q => q.id === questionId);
    this.setState({
      currentQuestionId: questionId,
      timeRemaining: question?.timeLimit || 0
    }, () => {
      this.clearExecutionState();
      this.updateURL();
      this.resetTimer();
    });
  };

  /**
   * Handle SQL query change
   */
  handleQueryChange = (sql) => {
    const { currentTestId, currentQuestionId, userQueries } = this.state;
    const updated = {
      ...userQueries,
      [currentTestId]: {
        ...userQueries[currentTestId],
        [currentQuestionId]: sql
      }
    };

    // Clear error and verdict when user starts typing
    this.setState({
      userQueries: updated,
      error: null,
      verdict: null
    });
    this.saveQueries(updated);
  };

  /**
   * Execute user query and compare with reference
   */
  handleExecute = async () => {
    const { currentTestId, currentQuestionId, userQueries } = this.state;
    const question = this.getCurrentQuestion();

    if (!question) {
      this.setState({ error: 'Question not found' });
      return;
    }

    const userSQL = userQueries[currentTestId][currentQuestionId];
    if (!userSQL || userSQL.trim() === '') {
      this.setState({ error: 'Please enter a SQL query' });
      return;
    }

    this.setState({ executing: true, error: null, verdict: null, diff: null });

    try {
      // Execute user query
      const userResults = executeQuery(userSQL);

      // Execute reference query
      const referenceResults = executeQuery(question.referenceSQL);

      // Compare results
      const comparison = compareResults(userResults, referenceResults);

      this.setState({
        queryResults: userResults,
        referenceResults,
        verdict: comparison.verdict,
        diff: comparison.diff,
        error: null,
        executing: false,
        chatOpen: comparison.verdict === 'pass' ? true : this.state.chatOpen
      });
    } catch (err) {
      this.setState({
        queryResults: null,
        referenceResults: null,
        verdict: null,
        diff: null,
        error: err.message,
        executing: false
      });
    }
  };

  /**
   * Reset editor to empty
   */
  handleReset = () => {
    this.handleQueryChange('');
    this.clearExecutionState();
    this.resetTimer();
  };

  /**
   * Load solution SQL
   */
  handleLoadSolution = () => {
    const question = this.getCurrentQuestion();

    if (question && question.solutionSQL) {
      this.handleQueryChange(question.solutionSQL);
    }
  };

  /**
   * Format SQL query
   */
  handleFormat = () => {
    const { currentTestId, currentQuestionId, userQueries } = this.state;
    const currentQuery = userQueries[currentTestId][currentQuestionId];

    if (!currentQuery || currentQuery.trim() === '') return;

    try {
      const formatted = format(currentQuery, {
        language: 'sqlite',
        tabWidth: 2,
        keywordCase: 'upper'
      });
      this.handleQueryChange(formatted);
    } catch (err) {
      // Ignore formatting errors (invalid SQL)
      console.warn('SQL formatting failed:', err.message);
    }
  };

  /**
   * Handle "Ask LLM" button click
   */
  handleAskLLM = async () => {
    const { currentTestId, currentQuestionId, userQueries } = this.state;
    const test = getTest(currentTestId);
    const question = this.getCurrentQuestion();
    const userQuery = userQueries[currentTestId]?.[currentQuestionId] || '';

    if (!userQuery.trim()) {
      this.setState({ error: 'Please write a SQL query before asking for feedback' });
      return;
    }

    // Build initial evaluation prompt
    const initialPrompt = `You are interviewing for an E7 Data Engineer position at FAANG or an elite AI lab (OpenAI, Anthropic, DeepMind etc). Evaluate the interviewee's answer to the given question.

Schema:
${test.schema}

Question: ${question.question}

User Answer:
${userQuery}

Provide constructive feedback on:
1. Correctness of the SQL logic
2. Query efficiency and optimization opportunities
3. Code readability and style
4. Edge cases or bugs

Be specific and actionable in your feedback.

Ignore the use of a static date in the query, that's part of the interview mock setup.
`;

    // Open chat and send initial prompt (hidden from UI)
    this.setState({ chatOpen: true, chatLoading: true, error: null });

    try {
      const response = await sendChatMessage([{
        role: 'user',
        content: initialPrompt
      }]);

      // Only add the AI response to chat (hide the initial prompt)
      const assistantMessage = {
        role: 'assistant',
        content: response.reply,
        timestamp: new Date().toISOString()
      };

      this.updateChatMessages([assistantMessage]);
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ chatLoading: false });
    }
  };

  /**
   * Handle sending a follow-up chat message
   */
  handleSendChatMessage = async (content) => {
    const { currentTestId, currentQuestionId, chatMessages } = this.state;
    const currentMessages = chatMessages[currentTestId]?.[currentQuestionId] || [];

    const userMessage = {
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };

    // Add user message immediately
    const updatedMessages = [...currentMessages, userMessage];
    this.updateChatMessages(updatedMessages);

    // Prepend the initial context (hidden) when sending to API
    const messagesToSend = this.prependInitialContext(updatedMessages);
    await this.sendMessageToAI(messagesToSend, updatedMessages);
  };

  /**
   * Prepend initial evaluation context to messages for API
   */
  prependInitialContext = (messages) => {
    const { currentTestId, currentQuestionId, userQueries } = this.state;
    const test = getTest(currentTestId);
    const question = this.getCurrentQuestion();
    const userQuery = userQueries[currentTestId]?.[currentQuestionId] || '';

    const initialPrompt = `You are interviewing for an E7 Data Engineer position at FAANG or an elite AI lab (OpenAI, Anthropic, DeepMind etc). Evaluate the interviewee's answer to the given question.

Schema:
${test.schema}

Question: ${question.question}

User Answer:
${userQuery}

Provide constructive feedback on:
1. Correctness of the SQL logic
2. Query efficiency and optimization opportunities
3. Code readability and style
4. Edge cases or bugs

Be specific and actionable in your feedback.

Ignore the use of a static date in the query, that's part of the interview mock setup.
`;

    return [
      { role: 'user', content: initialPrompt },
      ...messages
    ];
  };

  /**
   * Send messages to AI and get response
   * @param {Array} messagesToSend - Messages to send to API (may include hidden context)
   * @param {Array} messagesToSave - Messages to save to state (visible in UI)
   */
  sendMessageToAI = async (messagesToSend, messagesToSave = null) => {
    this.setState({ chatLoading: true, error: null });

    // If messagesToSave not provided, use messagesToSend
    const saveMessages = messagesToSave || messagesToSend;

    try {
      const response = await sendChatMessage(messagesToSend);

      const assistantMessage = {
        role: 'assistant',
        content: response.reply,
        timestamp: new Date().toISOString()
      };

      const updatedMessages = [...saveMessages, assistantMessage];
      this.updateChatMessages(updatedMessages);
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ chatLoading: false });
    }
  };

  /**
   * Update chat messages in state and localStorage
   */
  updateChatMessages = (messages) => {
    const { currentTestId, currentQuestionId, chatMessages } = this.state;

    const updated = {
      ...chatMessages,
      [currentTestId]: {
        ...chatMessages[currentTestId],
        [currentQuestionId]: messages
      }
    };

    this.setState({ chatMessages: updated });
    this.saveConversations(updated);
  };

  /**
   * Toggle chat panel open/closed
   */
  handleToggleChat = () => {
    this.setState(prevState => ({ chatOpen: !prevState.chatOpen }));
  };

  /**
   * Clear the current conversation
   */
  handleClearChat = () => {
    const { currentTestId, currentQuestionId, chatMessages } = this.state;

    const updated = {
      ...chatMessages,
      [currentTestId]: {
        ...chatMessages[currentTestId],
        [currentQuestionId]: []
      }
    };

    this.setState({ chatMessages: updated });
    this.saveConversations(updated);
  };

  /**
   * Format time remaining as MM:SS
   */
  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Render header with test name and database status
   */
  renderHeader = (test) => {
    const { dbInitialised, timeRemaining } = this.state;
    const isTimeRunningOut = timeRemaining > 0 && timeRemaining <= 60;

    return (
      <div className="sql-trainer-header mb3">
        <div>
          <h1 className="h1 mb1">{test.name}</h1>
          {test.subtitle && <p className="sql-trainer-subtitle">{test.subtitle}</p>}
        </div>
        <div className="sql-trainer-header-status">
          <div className="sql-trainer-status">
            <FontAwesomeIcon icon="database" className="mr1" />
            {dbInitialised ? (
              <span className="status-ready">Database ready</span>
            ) : (
              <span className="status-loading">Loading SQLite...</span>
            )}
          </div>
          <div className={`sql-trainer-timer ${isTimeRunningOut ? 'timer-warning' : ''} ${timeRemaining === 0 ? 'timer-expired' : ''}`}>
            <FontAwesomeIcon icon="clock" className="mr1" />
            <span>{this.formatTime(timeRemaining)}</span>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render sidebar navigation
   */
  renderSidebar = (testMetadata) => {
    const { currentTestId } = this.state;

    if (testMetadata.length <= 1) return null;

    return (
      <div className="sql-trainer-sidebar">
        <div className="sql-trainer-sidebar-title">Test Sets</div>
        {testMetadata.map(t => (
          <div
            key={t.id}
            className={`sql-trainer-sidebar-item ${t.id === currentTestId ? 'active' : ''}`}
            onClick={() => !this.isDisabled() && this.handleTestChange({ target: { value: t.id } })}
          >
            <span className="sql-trainer-sidebar-item-name">{t.name}</span>
            <span className="sql-trainer-sidebar-item-count">
              {t.questionCount} question{t.questionCount !== 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Render test selector dropdown (mobile only)
   */
  renderTestSelector = (testMetadata) => {
    const { currentTestId } = this.state;

    if (testMetadata.length <= 1) return null;

    return (
      <div className="sql-test-selector">
        <label htmlFor="test-select" className="sql-test-selector-label">
          Test:
        </label>
        <select
          id="test-select"
          className="sql-test-selector-dropdown"
          value={currentTestId}
          onChange={this.handleTestChange}
          disabled={this.isDisabled()}
        >
          {testMetadata.map(t => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.questionCount} question{t.questionCount !== 1 ? 's' : ''})
            </option>
          ))}
        </select>
      </div>
    );
  };

  /**
   * Render question tabs
   */
  renderQuestionTabs = (test) => {
    const { currentQuestionId } = this.state;

    return (
      <div className="question-tabs mt3 mb3">
        {test.questions.map(q => (
          <QuestionTab
            key={q.id}
            questionNum={q.id}
            difficulty={q.difficulty}
            active={q.id === currentQuestionId}
            onClick={() => this.handleQuestionChange(q.id)}
          />
        ))}
      </div>
    );
  };

  /**
   * Render question display with text and hint
   */
  renderQuestion = (question) => {
    return (
      <div className="question-display mb3">
        <div className="question-difficulty-badge">{question.difficulty}</div>
        <div className="question-text">{question.question}</div>
        {question.hint && (
          <div className="question-hint">
            <strong>Hint:</strong> {question.hint}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render control buttons (Execute, Reset, Format, Load Solution)
   */
  renderControls = (question) => {
    const { executing } = this.state;

    return (
      <div className="sql-controls mt2 mb3">
        <button
          className="sql-button primary"
          onClick={this.handleExecute}
          disabled={this.isDisabled()}
        >
          <FontAwesomeIcon icon="play" className="mr1" />
          {executing ? 'Executing...' : 'Execute & Check'}
        </button>
        <button
          className="sql-button secondary"
          onClick={this.handleReset}
          disabled={this.isDisabled()}
        >
          <FontAwesomeIcon icon="rotate-right" className="mr1" />
          Reset
        </button>
        <button
          className="sql-button secondary"
          onClick={this.handleFormat}
          disabled={this.isDisabled()}
        >
          Format
        </button>
        {question.solutionSQL && (
          <button
            className="sql-button secondary"
            onClick={this.handleLoadSolution}
            disabled={this.isDisabled()}
          >
            Load Solution
          </button>
        )}
      </div>
    );
  };

  /**
   * Render results grid with user and expected results
   */
  renderResults = () => {
    const { queryResults, referenceResults } = this.state;

    if (!queryResults && !referenceResults) return null;

    return (
      <div className="sql-results-grid">
        <SQLResultsTable
          results={queryResults}
          title="Your Results"
          emptyMessage="No rows returned"
        />
        <SQLResultsTable
          results={referenceResults}
          title="Expected Results"
          emptyMessage="No rows expected"
        />
      </div>
    );
  };

  render() {
    const {
      currentTestId,
      currentQuestionId,
      userQueries,
      verdict,
      error,
      chatMessages,
      chatOpen,
      chatLoading
    } = this.state;

    const test = getTest(currentTestId);
    const question = this.getCurrentQuestion();
    const testMetadata = getTestMetadata();

    if (!question) {
      return (
        <Container page="SQL Trainer">
          <div className="body">
            <div className="error">Question not found</div>
          </div>
        </Container>
      );
    }

    const currentQuery = userQueries[currentTestId][currentQuestionId] || '';

    return (
      <Container page="SQL Trainer" className="sql-trainer-page">
        <div className="body">
          {this.renderSidebar(testMetadata)}

          <div className="sql-trainer-content">
            {this.renderHeader(test)}
            {this.renderTestSelector(testMetadata)}

            {this.renderQuestionTabs(test)}
            {this.renderQuestion(question)}

            <SQLEditor
              value={currentQuery}
              onChange={this.handleQueryChange}
              onExecute={this.handleExecute}
              disabled={this.isDisabled()}
              verdict={verdict}
              error={error}
            />

            {this.renderControls(question)}

            {error && (
              <div className="sql-error mb3">
                <strong>Error:</strong> {error}
              </div>
            )}

            {verdict && !error && (
              <div className={`sql-verdict mb3 sql-verdict-${verdict}`}>
                {verdict === 'pass' && <><strong>Correct:</strong> Identical match.</>}
                {verdict === 'fail' && <><strong>Wrong:</strong> Differing data.</>}
                {verdict === 'close' && <><strong>Close:</strong> Correct data but incorrectly sorted.</>}
              </div>
            )}

            {this.renderResults()}

            <div className="sql-trainer-footer mt4">
              <p>
                Powered by <a href="https://sql.js.org/" target="_blank" rel="noopener noreferrer">sql.js</a>
                {' '}· All queries execute in your browser
              </p>
            </div>
          </div>

          <SchemaChatSplit
            schema={test.schema}
            pinnedNow={test.pinnedNow}
            chatMessages={chatMessages[currentTestId]?.[currentQuestionId] || []}
            onSendMessage={this.handleSendChatMessage}
            onAskLLM={this.handleAskLLM}
            onClearChat={this.handleClearChat}
            chatLoading={chatLoading}
            chatOpen={chatOpen}
            onChatToggle={this.handleToggleChat}
          />
        </div>
      </Container>
    );
  }
}

export default SQLTrainer;
